// Settings — admin user roster backed by /admin/settings/*.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { Avatar, PageHeader, ToolBtn } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { ApiError } from '@/lib/api';
import {
  fetchAdminSettingsUsers,
  createAdmin,
  type AdminSettingsUserRow,
} from '@/lib/admin-api';

const USER_COLUMNS: DataTableColumn<AdminSettingsUserRow>[] = [
  {
    key: 'name',
    h: 'Admin',
    cell: (r) => (
      <div className="flex items-center gap-2">
        <Avatar name={r.displayName} size={26} />
        <div className="min-w-0">
          <div className="truncate font-medium">{r.displayName}</div>
          <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">{r.email}</div>
        </div>
      </div>
    ),
  },
  { key: 'role', h: 'Role', cell: (r) => <StatusPill status={r.role} /> },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'twoFactor',
    h: '2FA',
    cell: (r) =>
      r.twoFactorEnabled ? (
        <span className="text-[12px] text-[#166534]">On</span>
      ) : (
        <span className="text-[12px] text-[color:var(--bo-mute)]">Off</span>
      ),
  },
  {
    key: 'createdAt',
    h: 'Joined',
    cell: (r) => new Date(r.createdAt).toLocaleDateString(),
  },
];

export function Settings() {
  const usersQ = useQuery({
    queryKey: ['admin', 'settings', 'users'],
    queryFn: ({ signal }) => fetchAdminSettingsUsers(signal),
  });

  const users = usersQ.data?.rows ?? [];

  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Settings & RBAC"
        sub={`${users.length.toLocaleString()} admins.`}
        actions={
          <ToolBtn icon="plus" onClick={() => setCreateOpen(true)}>
            Create admin
          </ToolBtn>
        }
      />
      {createdEmail ? (
        <div
          role="status"
          className="flex items-center justify-between rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] px-3 py-2 text-[12.5px] text-[#065f46]"
        >
          <span>
            Admin <strong>{createdEmail}</strong> created. They can sign in to the backoffice now
            with the password you set.
          </span>
          <button
            type="button"
            onClick={() => setCreatedEmail(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      {usersQ.error && (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load settings: {usersQ.error.message}
        </div>
      )}

      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="mb-3 text-[14px] font-semibold">Admin team</div>
        <DataTable
          columns={USER_COLUMNS}
          rows={users}
          emptyLabel={usersQ.isLoading ? 'Loading…' : 'No admins.'}
        />
      </div>

      {createOpen ? (
        <CreateAdminModal
          onClose={() => setCreateOpen(false)}
          onCreated={(email) => {
            setCreateOpen(false);
            setCreatedEmail(email);
            void queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'users'] });
          }}
        />
      ) : null}
    </div>
  );
}

function CreateAdminModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      createAdmin({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        password,
      }),
    onSuccess: (res) => onCreated(res.email),
  });

  const errorMessage = (() => {
    const err = mut.error;
    if (!err) return null;
    if (err instanceof ApiError) {
      const code =
        err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error?: unknown }).error)
          : null;
      if (code === 'email_taken') return 'A user with that email already exists.';
      if (code === 'invalid_payload') return 'Check the email and a password of at least 8 characters.';
    }
    return 'Could not create the admin. Please try again.';
  })();

  const emailOk = /\S+@\S+\.\S+/.test(email.trim());
  const passwordOk = password.length >= 8;
  const canSubmit = emailOk && passwordOk && !mut.isPending;
  const field =
    'h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)]';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Create admin"
      onClick={onClose}
    >
      <form
        className="w-full max-w-md space-y-3 rounded-xl border border-[color:var(--bo-line)] bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mut.mutate();
        }}
      >
        <div>
          <h2 className="text-[15px] font-semibold text-[color:var(--bo-ink)]">Create admin</h2>
          <p className="mt-0.5 text-[12px] text-[color:var(--bo-mute)]">
            Sets the password directly — the new admin can sign in to the backoffice right away.
          </p>
        </div>

        {errorMessage ? (
          <div className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Email *</span>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@drivecars.ai"
            className={field}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={field} />
          </label>
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={field} />
          </label>
        </div>

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Password *</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            className={field}
          />
          {password.length > 0 && !passwordOk ? (
            <span className="text-[11px] text-[#9f1239]">Use at least 8 characters.</span>
          ) : null}
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-9 rounded-lg bg-[color:var(--bo-ink)] px-3 text-[13px] font-medium text-white hover:bg-black disabled:opacity-50"
          >
            {mut.isPending ? 'Creating…' : 'Create admin'}
          </button>
        </div>
      </form>
    </div>
  );
}
